import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogIn, LogOut, User } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';


export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const publicNavLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/booking', label: 'Booking' },
    { path: '/contact', label: 'Contact' },
  ];

  const authenticatedNavLinks = [
    { path: '/pet-profile', label: 'Pet Profile' },
  ];

  const navLinks = user ? [...publicNavLinks.slice(0, 4), ...authenticatedNavLinks, ...publicNavLinks.slice(4)] : publicNavLinks;

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="bg-[#12233a] shadow-lg sticky top-0 z-50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo/Business Name */}
          <Link to="/" className="flex items-center gap-3">
            <motion.img
              whileHover={{
                scale: 1.1,
                rotate: [0, -10, 10, -10, 10, 0],
                transition: {
                  scale: { duration: 0.2 },
                  rotate: { duration: 0.6, ease: "easeInOut" }
                }
              }}
              whileTap={{
                scale: 0.95,
                rotate: [0, -10, 10, -10, 10, 0],
                transition: {scale: { duration: 0.2 },rotate: { duration: 0.6, ease: "easeInOut" }
                }
              }}
              src="/70dae82bf9d8825e2935f70db336503f184c22e2 (1).webp"
              alt="Wiggy's Wags and Wiggles Logo"
              className="h-14 w-auto cursor-pointer"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="relative"
              >
                <motion.span
                  whileHover={{ y: -2 }}
                  className={`transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'text-[#c8a96a]'
                      : 'text-[#f2eee8] hover:text-[#c8a96a]'
                  }`}
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}
                >
                  {link.label}
                </motion.span>
                {isActive(link.path) && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#c8a96a]"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}

            {/* Login/Logout Button - Desktop */}
            {user ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleLogout}
                className="flex items-center gap-2 bg-[#c8a96a] text-[#12233a] px-4 py-2 rounded-lg hover:bg-[#b89955] transition-colors shadow-soft hover:shadow-medium btn-ripple"
                style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}
              >
                <LogOut size={18} />
                Logout
              </motion.button>
            ) : (
              <Link to="/login">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-2 bg-[#c8a96a] text-[#12233a] px-4 py-2 rounded-lg hover:bg-[#b89955] transition-colors"
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}
                >
                  <LogIn size={18} />
                  Login
                </motion.button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            className="md:hidden text-[#f2eee8] hover:text-[#c8a96a] transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden pb-4 overflow-hidden"
            >
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className={`block py-3 px-4 rounded-lg transition-all duration-200 ${
                      isActive(link.path)
                        ? 'bg-[#c8a96a] text-[#12233a]'
                        : 'text-[#f2eee8] hover:bg-[#1a2b3c]'
                    }`}
                    style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}

              {/* Login/Logout Button - Mobile */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
                className="mt-2"
              >
                {user ? (
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 py-3 px-4 rounded-lg border-2 border-[#c8a96a] text-[#f2eee8] hover:bg-[#1a2b3c] transition-all duration-200"
                    style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="w-full flex items-center gap-2 py-3 px-4 rounded-lg border-2 border-[#c8a96a] text-[#f2eee8] hover:bg-[#1a2b3c] transition-all duration-200"
                    style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LogIn size={18} />
                    Login
                  </Link>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}
